import React from 'react';
import Header from '../components/Header';
import { useCountry } from '../contexts/CountryContext';
import BuyerPulseSubscribeCard from '../components/BuyerPulseSubscribeCard';
import PulseSocialProof from '../components/PulseSocialProof';
import { Activity, TrendingUp, ShieldCheck, Mail } from 'lucide-react';

/**
 * Pulse marketing page — public.
 * Sells the £4.99/mo (₹499 / C$8.99) email-only Pulse Top 10 subscription
 * to buyers. No dashboard widget — value lives in the inbox, every Monday.
 */
const PulsePage = () => {
  const { country } = useCountry();
  const cc = (country || 'UK').toUpperCase();

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="pulse-marketing-page">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <div className="text-center mb-10">
          <span className="text-[10px] tracking-[0.4em] text-[#E07A3A] uppercase font-medium">
            Shopperden Pulse Engine v1
          </span>
          <h1 className="font-['Playfair_Display'] text-4xl sm:text-5xl lg:text-6xl text-[#1A1A1A] mt-3 leading-tight">
            AI Real Estate Network — curated
            <br />
            <span className="text-[#1F3D2B]">GREEN-band matches in your inbox.</span>
          </h1>
          <p className="text-slate-600 max-w-2xl mx-auto mt-4 text-base sm:text-lg leading-relaxed">
            Shopperden's Pulse Engine grades every AI-Sourced Network Listing across UK, India and Canada on price growth, yield,
            liquidity and RERA/EPC compliance. Pay a small Matchmaking Service Fee — get only the winners. No app. No dashboard. No noise.
          </p>
        </div>

        <BuyerPulseSubscribeCard country={cc} dataTestId="pulse-page-subscribe-card" />

        {/* Real-only social proof — only renders when ≥ 5 paid subs per city */}
        <PulseSocialProof />

        {/* Beginner-friendly explainer — “What is a Pulse Rating?” */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 mt-10" data-testid="pulse-explainer-card">
          <span className="text-[10px] tracking-[0.4em] text-[#E07A3A] uppercase font-medium">
            New here? Read this first
          </span>
          <h2 className="font-['Playfair_Display'] text-2xl sm:text-3xl text-[#1A1A1A] mt-2">
            What is a Pulse Rating? <span className="text-[#1F3D2B]">In plain English.</span>
          </h2>
          <p className="text-slate-600 mt-3 leading-relaxed">
            Think of a Pulse Rating as a <strong>credit score for a property</strong>. Just like a credit
            score tells a lender how safe a borrower is, a Pulse Rating tells a buyer how
            <strong> safe and smart </strong> a property is to buy or rent — on a scale of <strong>0 to 100</strong>.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-6">
            <div className="rounded-xl border-l-4 border-emerald-500 bg-emerald-50 p-4">
              <div className="text-xs uppercase tracking-widest text-emerald-700 mb-1">Green · 70-100</div>
              <p className="text-sm text-slate-700">
                <strong>Strong buy.</strong> Prices rising · good rental yield · easy to resell ·
                paperwork (RERA / EPC) verified.
              </p>
            </div>
            <div className="rounded-xl border-l-4 border-amber-500 bg-amber-50 p-4">
              <div className="text-xs uppercase tracking-widest text-amber-700 mb-1">Amber · 40-69</div>
              <p className="text-sm text-slate-700">
                <strong>Worth a closer look.</strong> Solid in some areas, weaker in others.
                Get the report to see where.
              </p>
            </div>
            <div className="rounded-xl border-l-4 border-red-500 bg-red-50 p-4">
              <div className="text-xs uppercase tracking-widest text-red-700 mb-1">Red · 0-39</div>
              <p className="text-sm text-slate-700">
                <strong>Walk away.</strong> Falling prices · poor yield · slow market or
                missing legal docs.
              </p>
            </div>
          </div>

          <h3 className="font-medium text-[#1A1A1A] mt-8">How is it calculated?</h3>
          <p className="text-slate-600 mt-2 leading-relaxed">
            Our AI scans 5 things every Monday morning, gives each a score, then averages them:
          </p>
          <ul className="mt-3 space-y-2 text-sm text-slate-700">
            <li>📈 <strong>Price Growth</strong> — Are prices in this postcode rising or falling?</li>
            <li>💰 <strong>Rental Yield</strong> — How much rent could this property earn vs. its price?</li>
            <li>⚡ <strong>Liquidity</strong> — How fast do similar homes sell on the local market?</li>
            <li>🛡️ <strong>Compliance</strong> — Is RERA (India) or EPC (UK) paperwork actually filed?</li>
            <li>🔧 <strong>Local Pipeline</strong> — Are councils/RERA approving new infrastructure nearby?</li>
          </ul>

          <h3 className="font-medium text-[#1A1A1A] mt-8">Why pay £4.99 / month for it?</h3>
          <p className="text-slate-600 mt-2 leading-relaxed">
            Without a Pulse Rating you're guessing. One bad property purchase can cost you
            <strong> £20,000+</strong> in stamp duty, legal fees and lost deposits. Pulse subscribers
            get only the <strong>Top 10 Green-rated properties</strong> emailed to them every Monday —
            the <em>1% of listings</em> that actually pass all five filters. That's it. No noise.
            No app to check. Cancel any time, one click.
          </p>

          <p className="text-xs text-slate-400 mt-6">
            Shopperden Pulse Rating is decision-support data, not financial advice. Always
            consult a qualified solicitor or RERA-registered advisor before exchanging contracts.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
          <div className="luxury-card p-6">
            <Activity className="h-6 w-6 text-[#E07A3A] mb-3" />
            <h3 className="font-medium text-[#1A1A1A] mb-1">5-component score</h3>
            <p className="text-sm text-slate-500">Price growth · yield · liquidity · compliance · local pipeline</p>
          </div>
          <div className="luxury-card p-6">
            <TrendingUp className="h-6 w-6 text-[#E07A3A] mb-3" />
            <h3 className="font-medium text-[#1A1A1A] mb-1">Recomputed weekly</h3>
            <p className="text-sm text-slate-500">Every Monday at dawn — same as the digest send</p>
          </div>
          <div className="luxury-card p-6">
            <ShieldCheck className="h-6 w-6 text-[#E07A3A] mb-3" />
            <h3 className="font-medium text-[#1A1A1A] mb-1">RERA & EPC weighted</h3>
            <p className="text-sm text-slate-500">Compliance is 15% of the score — fakers don't make GREEN</p>
          </div>
        </div>

        <div className="mt-10 text-center text-xs text-slate-400">
          <Mail className="inline h-4 w-4 mr-1" />
          Email-only AI product. One-click unsubscribe. Shopperden is an AI Real Estate Network — not an estate agent or brokerage.
          Matchmaking Service Fee covers data &amp; intelligence only, never agency work.
        </div>
      </div>
    </div>
  );
};

export default PulsePage;
